import { useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import SellValuationCard from "@/components/valuation/SellValuationCard";
import SellPackagesGrid, { type SellPkg } from "@/components/valuation/SellPackagesGrid";
import { SELL_PACKAGES_COPY } from "@/components/valuation/sellPackagesCopy";
import { saveSellPrefill, type SellValuationSnapshot } from "@/lib/sellPrefill";
import { trackSellPackageSelect, trackSellValuationPublishClick } from "@/utils/buyerJourneyTracking";
import { useNavigate } from "@/lib/router-compat";
import { brandSlug, formatEur } from "@/lib/landingPages";
import type { SellBrandStats } from "@/lib/sellBrandPages";

const t = SELL_PACKAGES_COPY.ro;

/** RO-only sell-intent page for one brand (/vinde-masina/{marca}). All numbers come from the loader stats. */
export default function SellBrandPage({ stats }: { stats: SellBrandStats }) {
  const navigate = useNavigate();
  const lastValuation = useRef<SellValuationSnapshot | null>(null);
  const [highlight, setHighlight] = useState(false);
  const carsPath = `/cars/${brandSlug(stats.make)}`;

  const choose = (pkg: SellPkg) => {
    const v = lastValuation.current;
    trackSellPackageSelect(pkg, !!v);
    if (v) saveSellPrefill(v);
    try {
      sessionStorage.setItem("preferredPrivatePkg", pkg);
    } catch {}
    navigate(`/vinde-masina/metoda?pkg=${pkg}`);
  };

  return (
    <div className="min-h-screen bg-background">
      <main className="container mx-auto px-4 py-12 md:py-20">
        <div className="max-w-6xl mx-auto">
          <div className="surface-dark band-bleed -mt-12 md:-mt-20 py-6 md:py-10 text-center mb-10">
            <h1 className="text-3xl md:text-5xl font-bold text-foreground mb-4 leading-tight">
              Vinde mașina ta {stats.make}
            </h1>
            <p className="text-sm md:text-base text-muted-foreground max-w-2xl mx-auto">
              Acum sunt {stats.count.toLocaleString("ro-RO")} anunțuri active {stats.make} pe RO Auto Market.
              Evaluează-ți mașina gratuit și publică anunțul în câteva minute, fără comision la vânzare.
            </p>
          </div>

          {stats.models.length > 0 && (
            <section aria-labelledby="sell-brand-models" className="mb-10">
              <h2 id="sell-brand-models" className="font-serif text-2xl md:text-3xl text-foreground mb-4">
                Modele {stats.make} căutate acum
              </h2>
              <div className="overflow-x-auto rounded-lg border border-border">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border text-left text-muted-foreground">
                      <th className="px-4 py-3 font-medium">Model</th>
                      <th className="px-4 py-3 font-medium">Anunțuri active</th>
                      <th className="px-4 py-3 font-medium">Interval de preț</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.models.map((m) => (
                      <tr key={m.model} className="border-b border-border last:border-0">
                        <td className="px-4 py-3 text-foreground">{stats.make} {m.model}</td>
                        <td className="px-4 py-3 text-foreground">{m.count}</td>
                        <td className="px-4 py-3 text-foreground">
                          {m.minEur != null && m.maxEur != null
                            ? `${formatEur(m.minEur)} – ${formatEur(m.maxEur)}`
                            : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-2 text-xs text-muted-foreground">
                Intervalele sunt calculate din prețurile cerute în anunțurile active, convertite în euro.
              </p>
            </section>
          )}

          <SellValuationCard
            initial={{ make: stats.make }}
            initialKey={`brand-${stats.make}`}
            onResult={(snap) => { lastValuation.current = snap; }}
            onPublish={(snap) => {
              lastValuation.current = snap;
              trackSellValuationPublishClick(snap.has_estimate);
              setHighlight(true);
              document.getElementById("sell-packages")?.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
          />

          <SellPackagesGrid t={t} highlight={highlight} onChoose={choose} />

          <nav aria-label="Linkuri utile" className="mt-10 text-center text-sm">
            <ul className="flex flex-wrap justify-center gap-x-4 gap-y-2">
              <li>
                <Link to="/cars/$make" params={{ make: brandSlug(stats.make) }} className="text-primary underline underline-offset-4">
                  Vezi anunțurile {stats.make} de vânzare
                </Link>
              </li>
              <li>
                <a href="/evaluare-auto" className="text-primary underline underline-offset-4">
                  Cum funcționează evaluarea
                </a>
              </li>
              <li>
                <a href="/vinde-masina" className="text-primary underline underline-offset-4">
                  Vinde o mașină de altă marcă
                </a>
              </li>
            </ul>
          </nav>
        </div>
      </main>
      <footer className="border-t border-border px-4 py-6 text-center text-sm text-muted-foreground">
        <p>Operator: Vladimir Tudor, comerciant individual înregistrat în Regatul Unit.</p>
        <nav className="mt-2 flex flex-wrap justify-center gap-4" aria-label="Informații legale">
          <a href="/termeni-si-conditii" className="underline">Termeni și condiții</a>
          <a href="/politica-de-confidentialitate" className="underline">Politica de confidențialitate</a>
          <a href="/mentiuni-legale" className="underline">Mențiuni legale</a>
          <a href="/contact" className="underline">Contact</a>
        </nav>
      </footer>
    </div>
  );
}
